// src/lib/services/data/DataSyncService.ts
// Single entry point for all data state mutations from WebSocket messages.
// Handles DATA_SNAPSHOT, DATA_DELTA, and DATA_TICK events.

import {
	smartUpdateAlerts,
	smartUpdateWorkOrders,
	applyAlertDelta,
	applyAlertDeltaBatch,
	applyWorkOrderDelta,
	resetSnapshotTrackers
} from './smartUpdate';
import { domainState } from '$lib/state/index.svelte';
import { createLogger } from '$lib/utils/logger';
import { notifyRecoverySnapshot } from '$lib/state/connection-recovery.state.svelte';
import {
	shouldApply,
	record as recordSeq,
	bumpLiveGeneration,
	resetOrdering,
	initDeltaOrdering
} from './deltaOrdering';
import type { Alert } from '$lib/types/domain/alerts.types';

// Arm the reset-on-reconnect subscription as soon as the realtime data path
// loads (idempotent; browser-guarded inside).
initDeltaOrdering();

/**
 * Ordering context threaded from MessageRouter for live WS messages.
 *   seq    — parsed envelope sequence (undefined for legacy/REST/local)
 *   isLive — true only for messages that arrived over the live WebSocket, so
 *            local optimistic callers don't bump the live epoch.
 */
interface OrderingCtx {
	seq?: number;
	isLive?: boolean;
}

type DataHealthStatus = 'fresh' | 'stale' | 'empty_cache' | 'unavailable' | 'site_data_missing' | 'unknown';

const log = createLogger('DataSyncService');

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface BackendGroup {
	asset_id: string;
	asset_name: string;
	manager: string;
	alerts: Record<string, unknown>[];
	daily_data?: Record<string, unknown>[];
}

interface SnapshotPayload {
	regular_alerts?: BackendGroup[];
	approved_alerts?: BackendGroup[];
	work_orders?: unknown[];
	snapshot_ts?: string;
	[key: string]: unknown;
}

interface DeltaItem {
	[key: string]: unknown;
}

interface DiffEntry {
	added?: DeltaItem[];
	removed?: DeltaItem[];
	modified?: DeltaItem[];
}

interface TickDiff {
	alerts_diff?: DiffEntry;
	work_orders_diff?: DiffEntry;
}

// ---------------------------------------------------------------------------
// handleSnapshot
// ---------------------------------------------------------------------------

/**
 * Called when DATA_SNAPSHOT arrives.
 * Routes to alert or work-order update functions based on entity type.
 *
 * @param entity     - 'alerts' | 'work_orders' | 'all'
 * @param data       - Raw payload from the WebSocket message
 * @param dataStatus - Health status from backend ('fresh' | 'stale' | 'empty_cache' | 'unavailable' | 'unknown')
 * @param source     - Data pipeline source. 'live' for WebSocket, 'rest_fallback' for the HTTP backup
 *                     polling loop used when WebSocket is dead. Determines which phase the recovery
 *                     machine transitions into (CONNECTED for live, DEGRADED for rest_fallback).
 */
/**
 * Extract snapshot_ts from the message. The backend places it either:
 *   - at the top level of the message (streamlined_service.py)
 *   - inside `metadata` (data_broadcasters.py)
 *   - inside `data` (legacy path — still in the SnapshotPayload type
 *     for backward compat but not the canonical location)
 *
 * MessageRouter forwards the entire validated message, so we accept all
 * three locations and pick the first one that exists.
 */
function extractSnapshotTs(data: SnapshotPayload, rawMessage?: Record<string, unknown>): string | undefined {
	if (data && typeof data === 'object' && 'snapshot_ts' in data && typeof data.snapshot_ts === 'string') {
		return data.snapshot_ts;
	}
	if (rawMessage) {
		if (typeof rawMessage.snapshot_ts === 'string') return rawMessage.snapshot_ts;
		const meta = rawMessage.metadata as Record<string, unknown> | undefined;
		if (meta && typeof meta.snapshot_ts === 'string') return meta.snapshot_ts;
	}
	return undefined;
}

function handleSnapshot(
	entity: string,
	data: SnapshotPayload,
	// P11: default changed from 'fresh' to 'unknown'. A missing dataStatus
	// parameter means the caller did not thread the backend-reported
	// status through — which is either a legacy code path or a bug.
	// Either way, defaulting to 'fresh' is a lie; 'unknown' routes the
	// snapshot to DEGRADED which is the correct conservative default.
	dataStatus: DataHealthStatus = 'unknown',
	source: 'live' | 'rest_fallback' = 'live',
	rawMessage?: Record<string, unknown>
): void {
	const t0 = performance.now();
	let applied = false;

	if (entity === 'alerts') {
		const groupedRegular = data.regular_alerts ?? [];
		const groupedApproved = data.approved_alerts ?? [];
		const snapshotTs = extractSnapshotTs(data, rawMessage);
		applied = smartUpdateAlerts(groupedRegular, groupedApproved, snapshotTs, dataStatus);
	} else if (entity === 'work_orders') {
		const items = Array.isArray(data) ? (data as unknown[]) : (data.work_orders ?? []);
		// WO snapshot stale-rejection is only wired here, where a snapshot_ts is
		// available + validation-surviving. The combined 'all'/REST path passes
		// no snapshot_ts (gated by the REST request-epoch instead).
		const snapshotTs = extractSnapshotTs(data, rawMessage);
		applied = smartUpdateWorkOrders(items, dataStatus, snapshotTs);
	} else if (entity === 'all') {
		const groupedRegular = data.regular_alerts ?? [];
		const groupedApproved = data.approved_alerts ?? [];
		const snapshotTs = extractSnapshotTs(data, rawMessage);
		const alertsApplied = smartUpdateAlerts(groupedRegular, groupedApproved, snapshotTs, dataStatus);

		const workOrders = data.work_orders ?? [];
		const workOrdersApplied = smartUpdateWorkOrders(workOrders, dataStatus);

		// If EITHER domain applied, the snapshot counts as landed.
		applied = alertsApplied || workOrdersApplied;
	} else if (entity === 'hardware_alerts') {
		const items = (data as any).items ?? [];
		domainState.hardwareAlerts.items = items;
		domainState.hardwareAlerts.isLoading = false;
		domainState.hardwareAlerts.initialized = true;
		domainState.hardwareAlerts.lastUpdateTime = new Date();
		applied = true;
		log.info('Hardware alerts snapshot received', { count: items.length });
	} else {
		log.warn('handleSnapshot: unknown entity type — ignoring', { entity });
		return;
	}

	// Honor smartUpdate's return value. If the snapshot was rejected
	// (stale, empty overwrite, not an array), we do NOT notify the
	// recovery machine — that would be a lie. Previously this block
	// fired unconditionally, which meant a rejected stale snapshot
	// still pushed the machine to CONNECTED.
	if (!applied) {
		log.debug('handleSnapshot: snapshot rejected by smartUpdate, not notifying recovery machine', {
			entity,
			dataStatus,
			source
		});
		return;
	}

	// Notify the recovery machine that a snapshot landed. The machine
	// decides the phase based on source AND dataStatus:
	//
	//   source='live'          + dataStatus='fresh'    → CONNECTED
	//   source='live'          + dataStatus degraded   → DEGRADED (P4)
	//   source='rest_fallback' + any dataStatus        → DEGRADED (L3)
	notifyRecoverySnapshot(source, dataStatus);

	// A live snapshot that actually applied advances the live epoch so a
	// slower REST fallback response captured earlier is dropped rather than
	// clobbering this fresher data.
	if (source === 'live') {
		bumpLiveGeneration();
	}

	const elapsed = (performance.now() - t0).toFixed(1);
	log.debug('handleSnapshot complete', { entity, dataStatus, source, applied, elapsed: `${elapsed}ms` });
}

// ---------------------------------------------------------------------------
// handleDelta
// ---------------------------------------------------------------------------

/**
 * Called when DATA_DELTA arrives (single item change).
 *
 * @param entity - 'alerts' | 'approved_alerts' | 'work_orders'
 * @param action - 'add' | 'update' | 'remove'
 * @param data   - The item payload (single object or array)
 */
/**
 * P9: batch delta handler for alerts. Called by MessageRouter when a
 * DATA_DELTA_BATCH message arrives with multiple alert deltas. Routes
 * to applyAlertDeltaBatch which processes all deltas in a single pass
 * (O(N + K)) instead of calling applyAlertDelta per item (O(N * K)).
 *
 * Work-orders and hardware-alerts batches still go through per-item
 * handleDelta — their update cost is already O(K) because they don't
 * rebuild a grouped-by-asset structure.
 */
function handleAlertsDeltaBatch(
	items: Array<{ action: string; data: DeltaItem }>,
	ctx: OrderingCtx = {}
): void {
	if (items.length === 0) return;

	// Coalesce by alert_flag_id (last op wins) so the same id never appears
	// twice in one envelope — the per-entity guard then sees each id once and
	// the shared envelope seq can't reject a legit later item from the same
	// batch.
	const coalesced = new Map<string, { action: string; alert: Alert }>();
	for (const it of items) {
		if (!it.data || typeof it.data !== 'object') continue;
		const alert = it.data as unknown as Alert;
		const id = alert.alert_flag_id;
		if (!id) continue;
		coalesced.set(id, { action: it.action, alert });
	}
	if (coalesced.size === 0) return;

	// Per-entity stale-rejection: keep only deltas at/after the last applied seq.
	const kept: Array<{ action: string; alert: Alert }> = [];
	for (const [id, d] of coalesced) {
		if (shouldApply('alerts', id, ctx.seq)) {
			kept.push(d);
		} else {
			log.debug('Stale alert delta rejected (batch)', { id, seq: ctx.seq });
		}
	}
	if (kept.length === 0) {
		// Whole batch was stale — do NOT signal recovery (it would be a lie).
		return;
	}

	applyAlertDeltaBatch(kept);
	for (const d of kept) {
		recordSeq('alerts', d.alert.alert_flag_id, ctx.seq, d.action === 'remove');
	}

	// Proof-of-life: a non-empty delta batch arrived via WebSocket.
	// Mirrors the handleTick recovery signal — see comment there.
	if (ctx.isLive) bumpLiveGeneration();
	notifyRecoverySnapshot('live', 'fresh');
}

function handleDelta(
	entity: string,
	action: string,
	data: DeltaItem | DeltaItem[],
	ctx: OrderingCtx = {}
): void {
	let appliedAny = false;

	if (entity === 'alerts' || entity === 'approved_alerts') {
		// data may be a single alert or an array of alerts
		const alerts: DeltaItem[] = Array.isArray(data) ? data : [data];
		for (const alert of alerts) {
			const id = (alert as { alert_flag_id?: string }).alert_flag_id;
			if (!shouldApply('alerts', id, ctx.seq)) {
				log.debug('Stale alert delta rejected', { id, seq: ctx.seq });
				continue;
			}
			const applied = applyAlertDelta(
				action,
				alert as unknown as Parameters<typeof applyAlertDelta>[1]
			);
			if (applied) {
				recordSeq('alerts', id, ctx.seq, action === 'remove');
				appliedAny = true;
			}
		}
	} else if (entity === 'work_orders') {
		const incoming: DeltaItem[] = Array.isArray(data) ? data : [data];
		for (const item of incoming) {
			const id = item.id as string | undefined;
			if (id == null) continue; // cannot order/match a WO without an id
			if (!shouldApply('work_orders', id, ctx.seq)) {
				log.debug('Stale WO delta rejected', { id, seq: ctx.seq });
				continue;
			}
			applyWorkOrderDelta(action, item as Record<string, unknown>);
			// A valid-id delta that passed the guard is AUTHORITATIVE: record its
			// sequence (a remove is a tombstone) even if it targeted a row not
			// currently present. Otherwise a later lower-seq update could
			// resurrect a row this remove authoritatively deleted, and the REST
			// gate would miss this live write. (Alerts don't need this branch:
			// applyAlertDelta returns true on a no-op remove.)
			recordSeq('work_orders', id, ctx.seq, action === 'remove');
			appliedAny = true;
		}
	} else if (entity === 'hardware_alerts') {
		// Hardware alerts are not sequence-guarded (separate stream; no resurrect
		// race observed). Keep the existing inline behavior.
		const items = domainState.hardwareAlerts.items;

		if (action === 'remove') {
			const incoming = Array.isArray(data) ? data : [data];
			const removeIds = new Set(incoming.map((d) => d.id));
			domainState.hardwareAlerts.items = items.filter((ha: DeltaItem) => !removeIds.has(ha.id));
			appliedAny = true;
		} else if (action === 'add' || action === 'update') {
			const incoming = Array.isArray(data) ? data : [data];
			const updated = [...items];

			for (const newItem of incoming) {
				const idx = updated.findIndex((ha: DeltaItem) => ha.id === newItem.id);
				if (idx !== -1) {
					updated[idx] = { ...(updated[idx] as DeltaItem), ...newItem };
				} else {
					updated.push(newItem);
				}
			}

			domainState.hardwareAlerts.items = updated;
			appliedAny = true;
		} else {
			log.warn('handleDelta: unknown action for hardware_alerts', { action });
		}
	} else {
		log.warn('handleDelta: unknown entity type — ignoring', { entity });
		return;
	}

	if (!appliedAny) {
		// Everything was stale or a no-op — do NOT signal recovery (a rejected
		// stale delta is not proof of fresh live data).
		log.debug('handleDelta: nothing applied', { entity, action, seq: ctx.seq });
		return;
	}

	// Proof-of-life: a known-entity delta was successfully applied.
	// Same rationale as handleTick — live WebSocket data flowing through
	// here is the evidence the recovery machine uses to exit DEGRADED.
	if (ctx.isLive) bumpLiveGeneration();
	notifyRecoverySnapshot('live', 'fresh');
	log.debug('handleDelta complete', { entity, action });
}

// ---------------------------------------------------------------------------
// handleTick
// ---------------------------------------------------------------------------

/**
 * Called when DATA_TICK arrives (periodic diff broadcast from backend).
 * Applies all changes in the diff, then rebuilds grouped arrays once at the end.
 *
 * @param diff - Object with optional alerts_diff and work_orders_diff
 */
function handleTick(diff: TickDiff, ctx: OrderingCtx = {}): void {
	if (!diff) return;

	// Track whether we actually applied any change. A no-op tick (all
	// diff arrays empty) should NOT signal recovery — it's a heartbeat,
	// not a proof of fresh data. A tick whose items are ALL stale-rejected
	// is likewise not proof of fresh data.
	let appliedAny = false;

	// All items in one tick envelope share the envelope's sequence (ctx.seq).
	// Removes are processed before adds/updates so a same-tick re-file routes
	// correctly, matching the prior ordering.

	// --- Alerts diff ---
	if (diff.alerts_diff) {
		const alertsDiff = diff.alerts_diff;

		const toProcess: Array<{ action: string; items: DeltaItem[] }> = [];

		if (Array.isArray(alertsDiff.removed) && alertsDiff.removed.length > 0) {
			toProcess.push({ action: 'remove', items: alertsDiff.removed });
		}
		if (Array.isArray(alertsDiff.added) && alertsDiff.added.length > 0) {
			toProcess.push({ action: 'add', items: alertsDiff.added });
		}
		if (Array.isArray(alertsDiff.modified) && alertsDiff.modified.length > 0) {
			toProcess.push({ action: 'update', items: alertsDiff.modified });
		}

		for (const { action, items } of toProcess) {
			for (const alert of items) {
				const id = (alert as { alert_flag_id?: string }).alert_flag_id;
				if (!shouldApply('alerts', id, ctx.seq)) {
					log.debug('Stale alert tick item rejected', { id, seq: ctx.seq });
					continue;
				}
				const applied = applyAlertDelta(
					action,
					alert as unknown as Parameters<typeof applyAlertDelta>[1]
				);
				if (applied) {
					recordSeq('alerts', id, ctx.seq, action === 'remove');
					appliedAny = true;
				}
			}
		}
	}

	// --- Work orders diff ---
	if (diff.work_orders_diff) {
		const woDiff = diff.work_orders_diff;

		const toProcess: Array<{ action: string; items: DeltaItem[] }> = [];
		if (Array.isArray(woDiff.removed) && woDiff.removed.length > 0) {
			toProcess.push({ action: 'remove', items: woDiff.removed });
		}
		if (Array.isArray(woDiff.added) && woDiff.added.length > 0) {
			toProcess.push({ action: 'add', items: woDiff.added });
		}
		if (Array.isArray(woDiff.modified) && woDiff.modified.length > 0) {
			toProcess.push({ action: 'update', items: woDiff.modified });
		}

		for (const { action, items } of toProcess) {
			for (const item of items) {
				const id = item.id as string | undefined;
				if (id == null) continue;
				if (!shouldApply('work_orders', id, ctx.seq)) {
					log.debug('Stale WO tick item rejected', { id, seq: ctx.seq });
					continue;
				}
				// Authoritative valid-id delta — record (tombstone on remove) and
				// count even if a remove hit an absent row (see handleDelta).
				applyWorkOrderDelta(action, item as Record<string, unknown>);
				recordSeq('work_orders', id, ctx.seq, action === 'remove');
				appliedAny = true;
			}
		}
	}

	// Core recovery fix: DATA_TICK events are the only place live data
	// reaches the client after the initial bootstrap. Before this fix
	// they silently mutated domain state without signalling the recovery
	// machine, which meant any transient degradation (stale bootstrap
	// status, health blip, mis-routed rest_fallback dispatch) pinned
	// the machine in DEGRADED forever — no path back to CONNECTED
	// existed because nothing emitted a fresh-live signal after the
	// bootstrap snapshot.
	//
	// Emitting a notifyRecoverySnapshot here on a non-empty tick is the
	// proof-of-life the SNAPSHOT_RECEIVED reducer needs: source='live',
	// dataStatus='fresh' → transitions to CONNECTED and stops the
	// RestFallbackPoller. Empty heartbeats are suppressed so a steady
	// stream of no-op ticks can't mask a genuine backend outage.
	if (appliedAny) {
		if (ctx.isLive) bumpLiveGeneration();
		notifyRecoverySnapshot('live', 'fresh');
	}

	log.debug('handleTick complete', {
		hasAlertsDiff: !!diff.alerts_diff,
		hasWoDiff: !!diff.work_orders_diff,
		appliedAny
	});
}

// ---------------------------------------------------------------------------
// clearAll
// ---------------------------------------------------------------------------

/**
 * Called on logout. Resets alerts and workOrders state to empty defaults.
 */
function clearAll(): void {
	domainState.alerts.items = [];
	domainState.alerts.approvedItems = [];
	domainState.alerts.groupedItems = [];
	domainState.alerts.groupedApprovedItems = [];
	domainState.alerts.financialData = [];
	domainState.alerts.isLoading = true;
	domainState.alerts.initialized = false;
	domainState.alerts.dataHealth = 'fresh';

	domainState.workOrders.items = [];
	domainState.workOrders.isLoading = true;
	domainState.workOrders.initialized = false;

	domainState.hardwareAlerts.items = [];
	domainState.hardwareAlerts.isLoading = true;
	domainState.hardwareAlerts.initialized = false;
	domainState.hardwareAlerts.lastUpdateTime = null;

	// Reset realtime ordering state + snapshot freshness trackers on logout so a
	// new session never inherits stale sequence floors or timestamps.
	resetOrdering();
	resetSnapshotTrackers();

	log.debug('clearAll: domain state reset');
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const dataSyncService = {
	handleSnapshot,
	handleDelta,
	handleAlertsDeltaBatch,
	handleTick,
	clearAll
};
